import React, { type ReactNode } from "react";
import { Balancer } from "react-wrap-balancer";
import { Text, Flex, Icon, type IconProps, type FlexProps, type TextProps } from "@chakra-ui/react";
import { ArrowLink, type ArrowLinkProps } from "ui/link";

export type IconFeatureProps = {
  icon: typeof Icon;
  iconColor?: IconProps["color"];
  iconSize?: "md" | "lg";
  eyebrow?: string;
  title: string;
  titleFontSize?: TextProps["fontSize"];
  description: ReactNode;
  descriptionFontSize?: TextProps["fontSize"];
  skipDescriptionBalancing?: boolean;
  mobileJustify?: "center" | "start";
  cta?: {
    text: string;
    href: string;
    variant?: ArrowLinkProps["variant"];
  };
};

export function IconFeature({
  icon,
  iconColor,
  iconSize,
  eyebrow,
  title,
  titleFontSize,
  description,
  descriptionFontSize,
  cta,
  skipDescriptionBalancing,
  mobileJustify = "center",
  ...props
}: IconFeatureProps & FlexProps) {
  return (
    <Flex
      align={{ base: mobileJustify, md: "start" }}
      direction="column"
      h="100%"
      w="100%"
      maxW={{ base: "400px", md: "100%" }}
      mx="auto"
      textAlign={{ base: mobileJustify === "center" ? "center" : "left", md: "left" }}
      {...props}
    >
      <Icon
        as={icon}
        color={iconColor}
        boxSize={iconSize === "lg" ? { base: "48px", sm: "56px", lg: "72px" } : { base: "48px", sm: "60px" }}
        mb={iconSize === "lg" ? { base: 5, lg: 8 } : 5}
      />

      {eyebrow && (
        <Text fontSize={{ base: "sm", md: "md" }} textStyle="uppercase" mb={1}>
          {eyebrow}
        </Text>
      )}

      <Text textStyle="h7" as="h3" maxW="22ch" whiteSpace="pre-wrap" fontSize={titleFontSize}>
        <Balancer>{title}</Balancer>
      </Text>

      <Text mt={5} w="100%" fontSize={descriptionFontSize}>
        {skipDescriptionBalancing ? description : <Balancer>{description}</Balancer>}
      </Text>

      {cta && (
        <ArrowLink href={cta.href} variant={cta.variant ?? "grass"} pt={6} mt="auto">
          {cta.text}
        </ArrowLink>
      )}
    </Flex>
  );
}
